import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Sheet, SheetContent, SheetTitle } from "../ui/sheet";
import { ChangePasswordModal } from "../ChangePasswordModal";
import {
  LayoutDashboard,
  Users,
  ShieldAlert,
  FileSignature,
  Settings,
  ScrollText,
  UserPlus,
  LogOut,
  QrCode,
  Menu,
  KeyRound,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin"] },
  { to: "/visitor-logs", label: "Visitor Logs", icon: ScrollText, roles: ["admin"] },
  { to: "/lists", label: "Watch/Blacklist", icon: ShieldAlert, roles: ["admin"] },
  { to: "/briefings", label: "Briefings", icon: FileSignature, roles: ["admin"] },
  { to: "/users", label: "Users", icon: Users, roles: ["admin"] },
  { to: "/pre-register", label: "Pre-Register", icon: UserPlus, roles: ["employee", "admin"] },
  { to: "/settings", label: "Settings", icon: Settings, roles: ["admin"] },
  { to: "/audit-log", label: "Audit Log", icon: ScrollText, roles: ["admin"] },
  { to: "/kiosk", label: "Gate Kiosk", icon: QrCode, roles: ["admin", "security"] },
];

function SidebarNav({ items, user, onLogout, onNavigate, onChangePassword }) {
  return (
    <div className="flex flex-col h-full bg-brand-navy">
      <div className="p-6 border-b border-white/10">
        <img src="/brand/radish-logo-light.png" alt="Radish Technologies" className="h-8 w-auto mb-1" data-testid="sidebar-brand-logo" />
        <p className="text-sm text-slate-300 mt-1" data-testid="sidebar-user-info">
          {user?.name} · {user?.role}
        </p>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            data-testid={`nav-${item.to.replace("/", "")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors rounded-sm ${
                isActive ? "bg-brand-cyan text-brand-navy" : "text-slate-300 hover:bg-white/10 hover:text-white"
              }`
            }
          >
            <item.icon size={18} />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10 space-y-1">
        <button
          onClick={onChangePassword}
          data-testid="change-password-button"
          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-slate-300 hover:bg-white/10 hover:text-brand-cyan transition-colors rounded-sm"
        >
          <KeyRound size={18} /> Change Password
        </button>
        <button
          onClick={onLogout}
          data-testid="logout-button"
          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-slate-300 hover:bg-white/10 hover:text-red-400 transition-colors rounded-sm"
        >
          <LogOut size={18} /> Logout
        </button>
      </div>
    </div>
  );
}

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const items = NAV.filter((n) => n.roles.includes(user?.role));

  const handleLogout = async () => {
    setMobileNavOpen(false);
    await logout();
    navigate("/login");
  };

  const handleChangePassword = () => {
    setMobileNavOpen(false);
    setPasswordModalOpen(true);
  };

  return (
    <div className="flex min-h-screen bg-slate-50 text-brand-navy">
      <aside className="hidden md:flex w-64 shrink-0" data-testid="admin-sidebar">
        <SidebarNav items={items} user={user} onLogout={handleLogout} onChangePassword={handleChangePassword} />
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <div className="md:hidden flex items-center justify-between p-4 bg-brand-navy" data-testid="mobile-topbar">
          <img src="/brand/radish-logo-light.png" alt="Radish Technologies" className="h-6 w-auto" data-testid="mobile-topbar-logo" />
          <button
            onClick={() => setMobileNavOpen(true)}
            data-testid="mobile-nav-toggle"
            aria-label="Open navigation menu"
            className="text-white p-2 -mr-2"
          >
            <Menu size={22} />
          </button>
        </div>
        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="left" className="p-0 w-64 bg-brand-navy border-none" data-testid="mobile-nav-sheet">
          <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
          <SidebarNav items={items} user={user} onLogout={handleLogout} onNavigate={() => setMobileNavOpen(false)} onChangePassword={handleChangePassword} />
        </SheetContent>
      </Sheet>
      <ChangePasswordModal open={passwordModalOpen} onOpenChange={setPasswordModalOpen} hasPassword={user?.has_password !== false} />
    </div>
  );
}
